import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { COVER_MAX_WIDTH, DEFAULT_SIDEBAR_WIDTH, LayoutRow, Main, Panel, Spacer } from "./style";

// Matches the VoiceChat DisplayMode values, which this component only needs by name
const OPEN_MODE = "mounted";
const CLOSED_MODE = "widget";

// The chat log matters more than the avatar in a tall, narrow panel. VoiceChat ignores this in
// fullscreen, and it is not passed when the sidebar covers the screen (e.g. on a phone), where the
// face should stay prominent.
const SIDEBAR_AVATAR_SHARE = 0.3;
const SIDEBAR_SHARES_SCREEN_QUERY = `(min-width: ${COVER_MAX_WIDTH + 1}px)`;

const ChatSidebarContext = createContext(null);

const subscribeToViewport = onChange => {
  const query = window.matchMedia?.(SIDEBAR_SHARES_SCREEN_QUERY);
  query?.addEventListener("change", onChange);
  return () => query?.removeEventListener("change", onChange);
};
const sidebarSharesScreen = () => !!window.matchMedia?.(SIDEBAR_SHARES_SCREEN_QUERY).matches;
// Where the viewport cannot be queried it is taken as wide, so the remembered state still applies
const sidebarCoversScreen = () => !!window.matchMedia?.(`(max-width: ${COVER_MAX_WIDTH}px)`).matches;

// Storage can be unavailable (private windows, blocked site data) or hold garbage, so it only ever
// serves as a convenience and never prevents the page from rendering.
// Undefined where nothing usable is remembered, so that the default applies
const readOpen = storageKey => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey));
    return typeof stored === "boolean" ? stored : undefined;
  } catch {
    return undefined;
  }
};

const writeOpen = (storageKey, open) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(open));
  } catch {
    // Not remembering is fine
  }
};

/**
 * Lays out a page with a sidebar to the right that pushes the page content aside when open. A chat
 * rendered inside it that uses `useChatSidebar` shows as a widget when the sidebar is closed and
 * mounts into the sidebar when it is open. The open state is remembered between visits.
 *
 * The chat has to stay rendered at the same place in the tree for its conversation to survive
 * opening and closing, which is why it is rendered by the page as usual instead of by this layout.
 *
 * `width` is any CSS width, e.g. `clamp(360px, 28.85vw, 554px)`. It does not apply where the sidebar
 * covers the screen.
 *
 * `bottomOffset` is any CSS length of the page's own bar along the bottom of the window, which the sidebar
 * then stops above. It is handed on to what is inside the layout as `--voice-chat-bottom-offset`, which
 * the chat's widget also keeps clear of.
 *
 * `defaultOpen` is whether the sidebar starts open until the user has opened or closed it, which is then
 * remembered. It does not apply where the sidebar covers the screen.
 *
 * `border` separates the sidebar from the page by a thin line instead of a shadow.
 */
export const ChatSidebarLayout = ({
  storageKey,
  width = DEFAULT_SIDEBAR_WIDTH,
  bottomOffset = "0px",
  defaultOpen = false,
  border = false,
  children,
}) => {
  // NOTE: An open state, remembered or default, does not apply where the sidebar would cover the whole page on
  // load. A remembered one is not forgotten, so it still applies on a wide screen.
  const [open, setOpenState] = useState(() => (readOpen(storageKey) ?? defaultOpen) && !sidebarCoversScreen());
  const [slot, setSlot] = useState(null);
  const [chatCount, setChatCount] = useState(0);

  const setOpen = useCallback(
    nextOpen => {
      setOpenState(nextOpen);
      writeOpen(storageKey, nextOpen);
    },
    [storageKey]
  );

  const registerChat = useCallback(() => {
    setChatCount(count => count + 1);
    return () => setChatCount(count => count - 1);
  }, []);

  const value = useMemo(() => ({ open, setOpen, slot, registerChat }), [open, setOpen, slot, registerChat]);

  return (
    <ChatSidebarContext.Provider value={value}>
      <LayoutRow data-testid="chat-sidebar-layout" style={{ "--voice-chat-bottom-offset": bottomOffset }}>
        <Main>{children}</Main>
        {/* Only takes space on pages that have a chat, whatever state was remembered */}
        {chatCount > 0 && (
          <Spacer $open={open} $width={width}>
            <Panel
              data-testid="chat-sidebar"
              data-open={open}
              $open={open}
              $width={width}
              $border={border}
              ref={setSlot}
            />
          </Spacer>
        )}
      </LayoutRow>
    </ChatSidebarContext.Provider>
  );
};

/**
 * Props for a VoiceChat that lives in a `ChatSidebarLayout`, to spread over its other props.
 * Returns nothing to override outside of one, so the chat stays what its own props say. A chat that
 * renders nothing should pass `enabled: false` so the layout does not reserve a sidebar for it.
 */
export const useChatSidebar = ({ enabled = true } = {}) => {
  const context = useContext(ChatSidebarContext);
  const registerChat = context?.registerChat;

  useEffect(() => (enabled ? registerChat?.() : undefined), [enabled, registerChat]);

  const sharesScreen = useSyncExternalStore(subscribeToViewport, sidebarSharesScreen);
  const setOpen = context?.setOpen;
  // The chat only asks for its own mode (open) or the widget (minimized); anything else leaves the sidebar
  const setDisplayMode = useCallback(mode => setOpen?.(mode === OPEN_MODE), [setOpen]);

  if (!context) return {};
  return {
    displayMode: context.open ? OPEN_MODE : CLOSED_MODE,
    openDisplayMode: OPEN_MODE,
    mountInElement: context.slot ?? undefined,
    setDisplayMode,
    avatarShare: sharesScreen ? SIDEBAR_AVATAR_SHARE : undefined,
  };
};
